import type { NetSession } from './session';
import type { PeerId } from './transport';
import { hubService } from '../services/HubService';
import {
  HUB_PRESENCE_INTERVAL_MS,
  PresenceView,
  isHubPresence,
  type HubRosterMsg,
  type PresenceState,
} from './hubPresence';

/**
 * Host side of hub presence. Collects each client's latest presence, relays a
 * full roster (its own avatar + every client) at a fixed rate over the
 * unreliable channel, and exposes the clients' avatars for local rendering.
 */
export class HubPresenceHost {
  private clients = new Map<PeerId, PresenceState>();
  private lastSent = 0;
  public readonly view = new PresenceView();
  private offMsg: () => void;
  private offLeave: () => void;

  constructor(
    private session: NetSession,
    private localState: () => PresenceState,
  ) {
    this.offMsg = session.on('hub-presence', (from, m) => {
      if (isHubPresence(m)) {
        this.clients.set(from, { ...m.state, peerId: from });
      }
    });
    this.offLeave = session.onPeerLeave((id) => this.clients.delete(id));
  }

  clientStates(): PresenceState[] {
    return [...this.clients.values()];
  }

  broadcast(now: number): void {
    if (now - this.lastSent < HUB_PRESENCE_INTERVAL_MS) {
      return;
    }
    this.lastSent = now;
    const msg: HubRosterMsg = {
      type: 'hub-roster',
      t: now,
      hostMapId: hubService.mapId,
      count: this.session.peers().length + 1,
      roster: [this.localState(), ...this.clients.values()],
    };
    this.session.broadcast('unreliable', msg);
  }

  dispose(): void {
    this.offMsg();
    this.offLeave();
  }
}
